import { inject, injectable } from "inversify";
import "reflect-metadata";
import {
  AccountingBasisTypeModel,
  AuthorityToActTypeModel,
  ClientPaymentTypeModel,
  CountryCodeModel,
  CPLApiException,
  FileResponse,
  FreeholderCommissionRateTypeModel,
  FundScheduleChargeTypeModel,
  ICPLSelfServeDataImportSubmissionBundlesClient,
  ICPLSelfServeDataImportSubmissionEditsClient,
  ICPLSelfServeDataImportSubmissionFilesClient,
  ICPLSelfServeDataImportSubmissionLocksClient,
  ICPLSelfServeDataImportSubmissionColumnMappingsClient,
  ICPLSelfServeDataImportSubmissionsClient,
  ICPLSelfServeDataImportSubmissionSheetMappingsClient,
  ICPLSelfServeDataImportSubmissionValidationResultsClient,
  ICPLSettingsClient,
  ICPLTypesClient,
  ImportStatus,
  INewSelfServeDataImportSubmissionColumnMappingModel,
  INewSelfServeDataImportSubmissionSheetMappingModel,
  INewSelfServeDataImportSubmissionValidationResultModel,
  ListedTypeModel,
  INewSelfServeDataImportSubmissionColumnMappingsModel,
  NewSelfServeDataImportSubmissionColumnMappingModel,
  NewSelfServeDataImportSubmissionColumnMappingsBulkModel,
  NewSelfServeDataImportSubmissionColumnMappingsModel,
  INewSelfServeDataImportSubmissionEditModel,
  NewSelfServeDataImportSubmissionEditBatchModel,
  NewSelfServeDataImportSubmissionEditModel,
  NewSelfServeDataImportSubmissionEditsModel,
  NewSelfServeDataImportSubmissionModel,
  NewSelfServeDataImportSubmissionSheetMappingModel,
  NewSelfServeDataImportSubmissionSheetMappingsModel,
  NewSelfServeDataImportSubmissionValidationResultModel,
  PropertyServiceChargeTypeModel,
  PropertyTypeModel,
  SelfServeDataImportSubmissionEditsModel,
  SelfServeDataImportSubmissionFileModel,
  SelfServeDataImportSubmissionLockModel,
  SelfServeDataImportSubmissionColumnMappingModel,
  SelfServeDataImportSubmissionModel,
  SelfServeDataImportSubmissionSheetMappingModel,
  SelfServeDataImportSubmissionValidationResultModel,
  SelfServeDataImportWorksheetTypeModel,
  SubmissionStatus,
  TemplateType,
  UpdateSelfServeDataImportSubmissionStatusModel,
} from "services/api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

// Re-exported under the names the dataImport module already uses, so consumers stay
// decoupled from the generated client's naming.
export { ImportStatus, SubmissionStatus, TemplateType };
export type SubmissionModel = SelfServeDataImportSubmissionModel;
export type SubmissionFileModel = SelfServeDataImportSubmissionFileModel;
export type SheetMappingModel = SelfServeDataImportSubmissionSheetMappingModel;
export type ColumnMappingModel = SelfServeDataImportSubmissionColumnMappingModel;
export type SubmissionValidationResultModel = SelfServeDataImportSubmissionValidationResultModel;
export type WorksheetTypeModel = SelfServeDataImportWorksheetTypeModel;
export type NewSheetMapping = INewSelfServeDataImportSubmissionSheetMappingModel;
export type NewColumnMapping = INewSelfServeDataImportSubmissionColumnMappingModel;
export type NewSheetColumnMappings = Omit<INewSelfServeDataImportSubmissionColumnMappingsModel, "columnMappings"> & {
  columnMappings?: NewColumnMapping[];
};
export type NewSubmissionValidationResult = Omit<
  INewSelfServeDataImportSubmissionValidationResultModel,
  "submissionID"
>;
export type SubmissionEditsModel = SelfServeDataImportSubmissionEditsModel;
export type NewSubmissionEdit = INewSelfServeDataImportSubmissionEditModel;
export type SubmissionLockModel = SelfServeDataImportSubmissionLockModel;
export type { FileResponse };

export class SubmissionLockError extends Error {
  constructor(public readonly heldByUserName?: string) {
    super(
      heldByUserName
        ? `${heldByUserName} is currently editing this submission`
        : "This submission is being edited by another user"
    );
    this.name = "SubmissionLockError";
  }
}

// The BE guards wizard writes with EnsureLockHeldAndRenew; a lapsed lock comes back as a 509
// whose body says the editing session has expired. Business-rule 509s must not match.
function isExpiredSessionError(error: unknown): boolean {
  if (!(error instanceof CPLApiException) || error.status !== 509 || !error.response) {
    return false;
  }
  try {
    const parsed = JSON.parse(error.response);
    const message = typeof parsed === "string" ? parsed : parsed?.message;
    return typeof message === "string" && message.includes("editing session has expired");
  } catch {
    return error.response.includes("editing session has expired");
  }
}

export interface GetSubmissionsFilters {
  submissionStatus?: SubmissionStatus;
}

export interface DataImportReferenceData {
  accountTypes: string[];
  propertyTypes: PropertyTypeModel[];
  accountingBasisTypes: AccountingBasisTypeModel[];
  propertyServiceChargeTypes: PropertyServiceChargeTypeModel[];
  authorityToActTypes: AuthorityToActTypeModel[];
  listedTypes: ListedTypeModel[];
  freeholderCommissionRateTypes: FreeholderCommissionRateTypeModel[];
  clientPaymentTypes: ClientPaymentTypeModel[];
  fundScheduleChargeTypes: FundScheduleChargeTypeModel[];
  countries: CountryCodeModel[];
}

export interface IDataImportService {
  getSubmissions(filters?: GetSubmissionsFilters): Promise<SubmissionModel[]>;
  getSubmission(submissionID: string): Promise<SubmissionModel>;
  createSubmission(templateType: TemplateType, parentSubmissionID?: string): Promise<SubmissionModel>;
  getSubmissionFiles(submissionID: string): Promise<SubmissionFileModel[]>;
  uploadSubmissionFiles(submissionID: string, files: File[]): Promise<SubmissionFileModel[]>;
  deleteSubmissionFile(submissionFileID: string): Promise<void>;
  downloadSubmissionFile(submissionFileID: string): Promise<FileResponse>;
  updateSubmissionStep(
    submissionID: string,
    submissionStatus: SubmissionStatus,
    currentStep: number
  ): Promise<SelfServeDataImportSubmissionModel>;
  closeSubmission(submissionID: string): Promise<SelfServeDataImportSubmissionModel>;
  getReferenceData(): Promise<DataImportReferenceData>;
  getWorksheetTypes(templateTypeID?: number): Promise<WorksheetTypeModel[]>;
  getSheetMappings(submissionID: string): Promise<SheetMappingModel[]>;
  saveSheetMappings(submissionID: string, sheetMappings: NewSheetMapping[]): Promise<SheetMappingModel[]>;
  getColumnMappings(submissionID: string): Promise<ColumnMappingModel[]>;
  saveColumnMappings(
    submissionID: string,
    sheetColumnMappings: NewSheetColumnMappings[]
  ): Promise<ColumnMappingModel[]>;
  getValidationResult(submissionID: string): Promise<SubmissionValidationResultModel>;
  saveValidationResult(
    submissionID: string,
    result: NewSubmissionValidationResult
  ): Promise<SubmissionValidationResultModel>;
  saveEdits(submissionID: string, version: number, entries: NewSubmissionEdit[]): Promise<SubmissionEditsModel>;
  getEdits(submissionID: string): Promise<SubmissionEditsModel>;
  submitSubmission(submissionID: string, bundle: Blob, bundleFileName: string): Promise<SubmissionModel>;
  downloadSubmissionBundle(submissionID: string): Promise<FileResponse>;
  getTemplateUrls(): Promise<TemplateUrls>;
  acquireLock(submissionID: string): Promise<SubmissionLockModel>;
  releaseLock(submissionID: string): Promise<void>;
}

export interface TemplateUrls {
  staticDataTemplateUrl?: string;
  balanceImportTemplateUrl?: string;
}

@injectable()
export class DataImportService implements IDataImportService {
  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionsClient)
  cplSelfServeDataImportSubmissionsClient!: ICPLSelfServeDataImportSubmissionsClient;

  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionFilesClient)
  cplSelfServeDataImportSubmissionFilesClient!: ICPLSelfServeDataImportSubmissionFilesClient;

  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionSheetMappingsClient)
  cplSelfServeDataImportSubmissionSheetMappingsClient!: ICPLSelfServeDataImportSubmissionSheetMappingsClient;

  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionColumnMappingsClient)
  cplSelfServeDataImportSubmissionColumnMappingsClient!: ICPLSelfServeDataImportSubmissionColumnMappingsClient;

  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionValidationResultsClient)
  cplSelfServeDataImportSubmissionValidationResultsClient!: ICPLSelfServeDataImportSubmissionValidationResultsClient;
  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionEditsClient)
  cplSelfServeDataImportSubmissionEditsClient!: ICPLSelfServeDataImportSubmissionEditsClient;
  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionBundlesClient)
  cplSelfServeDataImportSubmissionBundlesClient!: ICPLSelfServeDataImportSubmissionBundlesClient;
  @inject(SERVICE_TYPES.CPLSelfServeDataImportSubmissionLocksClient)
  cplSelfServeDataImportSubmissionLocksClient!: ICPLSelfServeDataImportSubmissionLocksClient;

  @inject(SERVICE_TYPES.CPLTypesClient) cplTypesClient!: ICPLTypesClient;
  @inject(SERVICE_TYPES.CPLSettingsClient) cplSettingsClient!: ICPLSettingsClient;

  async getReferenceData(): Promise<DataImportReferenceData> {
    const [
      accountTypes,
      propertyTypes,
      accountingBasisTypes,
      propertyServiceChargeTypes,
      authorityToActTypes,
      listedTypes,
      freeholderCommissionRateTypes,
      clientPaymentTypes,
      fundScheduleChargeTypes,
      countries,
    ] = await Promise.all([
      this.cplTypesClient.getAccountTypes(),
      this.cplTypesClient.getPropertyTypes(),
      this.cplTypesClient.getAccountingBasisTypes(),
      this.cplTypesClient.getPropertyServiceChargeTypes(),
      this.cplTypesClient.getAuthorityToActTypes(),
      this.cplTypesClient.getListedTypes(),
      this.cplTypesClient.getFreeholderCommissionRateTypes(),
      this.cplTypesClient.getClientPaymentTypes(),
      this.cplTypesClient.getFundScheduleChargeTypes(),
      this.cplTypesClient.getCountries(),
    ]);

    return {
      accountTypes,
      propertyTypes,
      accountingBasisTypes,
      propertyServiceChargeTypes,
      authorityToActTypes,
      listedTypes,
      freeholderCommissionRateTypes,
      clientPaymentTypes,
      fundScheduleChargeTypes,
      countries,
    };
  }

  async getSubmissions(filters?: GetSubmissionsFilters): Promise<SubmissionModel[]> {
    // index/pageSize deliberately unset — the unpaged call returns the complete list (api-ref.md),
    // which the landing page splits in-progress vs past client-side.
    return this.cplSelfServeDataImportSubmissionsClient.getSubmissions(filters?.submissionStatus);
  }

  async getSubmission(submissionID: string): Promise<SelfServeDataImportSubmissionModel> {
    return this.cplSelfServeDataImportSubmissionsClient.getSubmission(submissionID);
  }

  async createSubmission(
    templateType: TemplateType,
    parentSubmissionID?: string
  ): Promise<SelfServeDataImportSubmissionModel> {
    // parentSubmissionID links a resubmission back to the submission it supersedes.
    return this.cplSelfServeDataImportSubmissionsClient.createSubmission(
      new NewSelfServeDataImportSubmissionModel({ templateType, parentSubmissionID })
    );
  }

  async getSubmissionFiles(submissionID: string): Promise<SubmissionFileModel[]> {
    // Oldest first, deleted rows excluded. Re-uploads never overwrite, so the same file name can
    // appear more than once — the latest row is the current one.
    return this.cplSelfServeDataImportSubmissionFilesClient.getFiles(submissionID);
  }

  async uploadSubmissionFiles(submissionID: string, files: File[]): Promise<SubmissionFileModel[]> {
    // One request for the whole batch — UploadFiles takes a repeated Files form field per file
    // and returns one created row per file, oldest first.
    return this.withLockRetry(submissionID, async () => {
      const uploaded = await this.cplSelfServeDataImportSubmissionFilesClient.uploadFiles({
        submissionID,
        files: files.map((file) => ({ data: file, fileName: file.name })),
      });

      if (uploaded.length < files.length) {
        throw new Error("Upload returned fewer file records than files sent");
      }
      return uploaded;
    });
  }

  async deleteSubmissionFile(submissionFileID: string): Promise<void> {
    return this.cplSelfServeDataImportSubmissionFilesClient.deleteFile(submissionFileID);
  }

  async downloadSubmissionFile(submissionFileID: string): Promise<FileResponse> {
    return this.cplSelfServeDataImportSubmissionFilesClient.downloadFile(submissionFileID);
  }

  async updateSubmissionStep(
    submissionID: string,
    submissionStatus: SubmissionStatus,
    currentStep: number
  ): Promise<SelfServeDataImportSubmissionModel> {
    // Same-status transitions are allowed, so passing the caller's status makes this a step-only
    // save. UpdateStatus rejects Submitted outright (that transition belongs to the Submit
    // endpoint), so refuse it before the round-trip.
    if (submissionStatus === SubmissionStatus.Submitted) {
      throw new Error("A submitted submission can no longer be changed");
    }
    return this.withLockRetry(submissionID, () =>
      this.cplSelfServeDataImportSubmissionsClient.updateStatus(
        new UpdateSelfServeDataImportSubmissionStatusModel({ submissionID, submissionStatus, currentStep })
      )
    );
  }

  async closeSubmission(submissionID: string): Promise<SelfServeDataImportSubmissionModel> {
    return this.cplSelfServeDataImportSubmissionsClient.closeSubmission(submissionID);
  }

  async getWorksheetTypes(templateTypeID?: number): Promise<SelfServeDataImportWorksheetTypeModel[]> {
    // The FE only knows the TemplateType string enum, not the numeric templateTypeID, so the
    // usual call omits the filter and matches worksheet types by name.
    return this.cplTypesClient.getSelfServeDataImportWorksheetTypes(templateTypeID);
  }

  async getSheetMappings(submissionID: string): Promise<SelfServeDataImportSubmissionSheetMappingModel[]> {
    return this.cplSelfServeDataImportSubmissionSheetMappingsClient.getSheetMappings(submissionID);
  }

  async saveSheetMappings(
    submissionID: string,
    sheetMappings: NewSheetMapping[]
  ): Promise<SelfServeDataImportSubmissionSheetMappingModel[]> {
    // SaveSheetMappings replaces the submission's full sheet mapping selection in one call.
    return this.withLockRetry(submissionID, () =>
      this.cplSelfServeDataImportSubmissionSheetMappingsClient.saveSheetMappings(
        new NewSelfServeDataImportSubmissionSheetMappingsModel({
          submissionID,
          sheetMappings: sheetMappings.map(
            (sheetMapping) => new NewSelfServeDataImportSubmissionSheetMappingModel(sheetMapping)
          ),
        })
      )
    );
  }

  async saveEdits(
    submissionID: string,
    version: number,
    entries: NewSubmissionEdit[]
  ): Promise<SelfServeDataImportSubmissionEditsModel> {
    // SaveEdits replaces the submission's whole edits journal — callers always send the
    // complete set (an emptied journal is saved as an empty entries list).
    return this.withLockRetry(submissionID, () =>
      this.cplSelfServeDataImportSubmissionEditsClient.saveEdits(
        new NewSelfServeDataImportSubmissionEditsModel({
          submissionID,
          edits: new NewSelfServeDataImportSubmissionEditBatchModel({
            version,
            entries: entries.map((entry) => new NewSelfServeDataImportSubmissionEditModel(entry)),
          }),
        })
      )
    );
  }

  async getColumnMappings(submissionID: string): Promise<SelfServeDataImportSubmissionColumnMappingModel[]> {
    return this.cplSelfServeDataImportSubmissionColumnMappingsClient.getColumnMappings(submissionID);
  }

  async saveColumnMappings(
    submissionID: string,
    sheetColumnMappings: NewSheetColumnMappings[]
  ): Promise<SelfServeDataImportSubmissionColumnMappingModel[]> {
    // BulkSaveColumnMappings replaces the submission's column mappings for every supplied sheet mapping in
    // one call, stamping each sheet mapping's columnMappingsConfirmed.
    return this.withLockRetry(submissionID, () =>
      this.cplSelfServeDataImportSubmissionColumnMappingsClient.bulkSaveColumnMappings(
        new NewSelfServeDataImportSubmissionColumnMappingsBulkModel({
          submissionID,
          sheetMappings: sheetColumnMappings.map(
            (sheetMapping) =>
              new NewSelfServeDataImportSubmissionColumnMappingsModel({
                submissionSheetMappingID: sheetMapping.submissionSheetMappingID,
                columnMappingsConfirmed: sheetMapping.columnMappingsConfirmed,
                columnMappings: sheetMapping.columnMappings?.map(
                  (columnMapping) => new NewSelfServeDataImportSubmissionColumnMappingModel(columnMapping)
                ),
              })
          ),
        })
      )
    );
  }

  async getValidationResult(submissionID: string): Promise<SelfServeDataImportSubmissionValidationResultModel> {
    return this.cplSelfServeDataImportSubmissionValidationResultsClient.getValidationResult(submissionID);
  }

  async saveValidationResult(
    submissionID: string,
    result: NewSubmissionValidationResult
  ): Promise<SelfServeDataImportSubmissionValidationResultModel> {
    return this.withLockRetry(submissionID, () =>
      this.cplSelfServeDataImportSubmissionValidationResultsClient.saveValidationResult(
        new NewSelfServeDataImportSubmissionValidationResultModel({ ...result, submissionID })
      )
    );
  }

  async getEdits(submissionID: string): Promise<SelfServeDataImportSubmissionEditsModel> {
    return this.cplSelfServeDataImportSubmissionEditsClient.getEdits(submissionID);
  }

  async submitSubmission(
    submissionID: string,
    bundle: Blob,
    bundleFileName: string
  ): Promise<SelfServeDataImportSubmissionModel> {
    // Uploads the CSV bundle; rejections carry the reason in the response body.
    return this.withLockRetry(submissionID, () =>
      this.cplSelfServeDataImportSubmissionBundlesClient.submit({
        submissionID,
        file: { data: bundle, fileName: bundleFileName },
      })
    );
  }

  async downloadSubmissionBundle(submissionID: string): Promise<FileResponse> {
    return this.cplSelfServeDataImportSubmissionBundlesClient.downloadBundle(submissionID);
  }

  async getTemplateUrls(): Promise<TemplateUrls> {
    const model = await this.cplSettingsClient.dataImportUrlTemplates();
    return {
      staticDataTemplateUrl: model.staticDataImportTemplateURL,
      balanceImportTemplateUrl: model.balanceImportTemplateURL,
    };
  }

  async acquireLock(submissionID: string): Promise<SelfServeDataImportSubmissionLockModel> {
    // Always 200: creates, renews, or takes over an expired lock. A live lock held by another
    // user comes back with isHeldByCurrentUser false — denial is in the payload, not the status.
    return this.cplSelfServeDataImportSubmissionLocksClient.acquireLock(submissionID);
  }

  async releaseLock(submissionID: string): Promise<void> {
    return this.cplSelfServeDataImportSubmissionLocksClient.releaseLock(submissionID);
  }

  // An expired editing session means our lock lapsed but nobody necessarily took it: re-acquire
  // once and replay the write. If the re-acquire shows another user now holds the lock, surface
  // who. Any other error (including business-rule 509s) passes through untouched.
  private async withLockRetry<T>(submissionID: string, write: () => Promise<T>): Promise<T> {
    try {
      return await write();
    } catch (error) {
      if (!isExpiredSessionError(error)) {
        throw error;
      }
      const lock = await this.acquireLock(submissionID);
      if (!lock.isHeldByCurrentUser) {
        throw new SubmissionLockError(lock.heldByUserName ?? undefined);
      }
      return await write();
    }
  }
}
